const owners = new WeakMap();

/** Local match/miss feedback consumes only the visible Twin Trinkets view and
 * accepted events. It never reads a solution, changes score or moves a target.
 * Register every cancellation with the enclosing round's presentation owner. */
export function presentTrinketFeedback({ board, view, state, reduced }, register) {
  if (!board?.isConnected || state.gameId !== 'spot-pair') return;
  const miss = view.events?.find((event) => event.type === 'miss');
  const solved =
    (view.stageComplete || state.completed) &&
    view.events?.some((event) => ['stage-complete', 'complete'].includes(event.type));
  const selected = state.data.selected;
  const visible = view.cells?.[selected];
  const indexes =
    miss?.cells ||
    (solved && Number.isInteger(visible?.value) && visible.value >= 0
      ? view.cells.flatMap((cell, index) => (cell.value === visible.value ? [index] : []))
      : []);
  if (indexes.length !== 2 || new Set(indexes).size !== 2) return;
  const artwork = indexes.map(
    (index) =>
      Number.isInteger(index) && board.querySelector(`[data-cell="${index}"] .game-trinket-art`),
  );
  if (artwork.some((node) => !node)) return;
  const token = {};
  const animations = [];
  const kind = miss ? 'trinketMiss' : 'trinketPair';
  const clean = () => {
    for (const node of artwork)
      if (owners.get(node) === token) {
        delete node.dataset[kind];
        owners.delete(node);
      }
  };
  for (const node of artwork) {
    delete node.dataset.trinketMiss;
    delete node.dataset.trinketPair;
    owners.set(node, token);
    node.dataset[kind] = 'true';
  }
  const bounds = artwork.map((node) => node.getBoundingClientRect());
  const centre = bounds.reduce(
    (point, rect) => ({
      x: point.x + (rect.left + rect.width / 2) / 2,
      y: point.y + (rect.top + rect.height / 2) / 2,
    }),
    { x: 0, y: 0 },
  );
  if (!reduced)
    for (const [index, node] of artwork.entries()) {
      if (typeof node.animate !== 'function') continue;
      const rect = bounds[index];
      const x = centre.x - rect.left - rect.width / 2;
      const y = centre.y - rect.top - rect.height / 2;
      const distance = Math.hypot(x, y) || 1;
      const dx = (x / distance) * Math.min(14, distance * 0.22);
      const dy = (y / distance) * Math.min(14, distance * 0.22);
      animations.push(
        node.animate(
          miss
            ? [
                { transform: 'translateX(0)', opacity: 1 },
                { transform: 'translateX(-3px)', opacity: 0.65, offset: 0.25 },
                { transform: 'translateX(3px)', opacity: 1, offset: 0.55 },
                { transform: 'translateX(0)', opacity: 1 },
              ]
            : [
                { transform: 'translate(0,0) scale(1)' },
                { transform: `translate(${dx}px,${dy}px) scale(1.12)`, offset: 0.45 },
                { transform: 'translate(0,0) scale(1)' },
              ],
          { duration: miss ? 300 : 580, easing: 'cubic-bezier(.2,.8,.3,1)' },
        ),
      );
    }
  register({
    cancel() {
      for (const animation of animations) animation.cancel();
      clean();
    },
  });
  if (miss && animations.length)
    void Promise.allSettled(animations.map((animation) => animation.finished)).then(clean);
}
